import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { reduxForm, initialize } from 'redux-form';

import type LocationRequestModel from 'ab-requestModels/location.requestModel';

import type { RootState } from 'af-reducers';

import { toRequestModelWithRawPhoneNumbers, toRequestModelWithFormattedPhoneNumbers } from 'af-utils/form.util';

import { LOCATION_EDIT } from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import Breadcrumbs from 'af-components/Breadcrumbs';
import EditLocationForm from 'af-components/SharedForms/Resources/Location';
import { validate } from 'af-components/SharedForms/Resources/Location/validation';

import * as LocationActions from 'af-actions/location';

import { fromVMtoRM } from './formModel';

interface PathParams {
	id: string;
}

type OwnProps = CustomRouteComponentProps<PathParams>;

type FormOwnProps = OwnProps & ConnectedProps<typeof connector>;

type Props = FormOwnProps & InjectedFormProps<LocationRequestModel, FormOwnProps>;

const Edit: React.FC<Props> = (props) => {
	const { initializeForm, findById, match: { params: { id } }, history, update, location: { state: { orgAlias } }, companyName } = props;

	// On mount
	React.useEffect(() => {
		const getData = async () => {
			const location = await findById(id);
			const form = toRequestModelWithFormattedPhoneNumbers(
				fromVMtoRM(location),
				['phone', 'phoneCountryCode'],
				['fax', 'faxCountryCode']
			);
			initializeForm(LOCATION_EDIT, form);
		};
		getData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const editLocation = React.useCallback(async (form: LocationRequestModel) => {
		const data: LocationRequestModel = toRequestModelWithRawPhoneNumbers(form, 'phone', 'fax');
		await update(data);
		history.push(CLIENT.COMPANY.RESOURCES.LOCATION.LIST(orgAlias, companyName));
	}, [companyName, history, orgAlias, update]);

	return (
		<div className="form-segment">
			<Breadcrumbs
				items={[
					{ label: 'Location', url: CLIENT.COMPANY.RESOURCES.LOCATION.LIST(orgAlias, companyName) },
					{ label: 'Preview', url: CLIENT.COMPANY.RESOURCES.LOCATION.PREVIEW(id, orgAlias, companyName) },
					{ label: 'Edit' },
				]}
			/>
			<EditLocationForm
				{...props}
				onSubmit={editLocation}
			/>
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		findById: LocationActions.findById,
		update: LocationActions.update,
		initializeForm: initialize,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	React.memo,
	connector,
	reduxForm<LocationRequestModel, FormOwnProps>({ form: LOCATION_EDIT, validate })
);

export default enhance(Edit);
